import React, { ReactNode, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { LoginCredentials } from "./interfaces";
import { useAuth } from "../../context/AuthProvider";
import Icon from "@mdi/react";
import { mdiAccount, mdiLock } from "@mdi/js";
import { Logo } from "../../components/svg/Logo";
import { Locale, useLocale } from "../../context/LocalizationProvider";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Link } from "../../components/common/Link";
import {
	DynamicRoutes,
	MvcSiteRoutes,
	StaticRoutes,
	useIsKnownFrontRouteForUser,
} from "../../routes";
import { UserRole } from "../../interfaces/UserRole";
import { useReturnUrlFromUrlParameter } from "../../hooks/useReturnUrl";
import { purgeLocalStorage } from "../../util/localStorage";
import { isDevEnv } from "../../util/environment";

function Login() {
	const { login, user, error } = useAuth();
	const [loginError, setLoginError] = useState(error);
	const navigate = useNavigate();
	const redirectUrl = useReturnUrlFromUrlParameter();
	const isKnownFrontRoute = useIsKnownFrontRouteForUser(user, redirectUrl);
	useEffect(() => {
		if (user) {
			if (redirectUrl) {
				if (isKnownFrontRoute) {
					navigate(redirectUrl, { replace: true });
				} else {
					window.location.assign(decodeURIComponent(redirectUrl));
				}
			} else {
				user.role === UserRole.Administrator
					? window.location.assign(`${MvcSiteRoutes.FrontPage}`)
					: navigate(StaticRoutes.FrontPage);
			}
		} else {
			purgeLocalStorage();
		}
	}, [user, navigate, redirectUrl, isKnownFrontRoute]);
	const {
		watch,
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<LoginCredentials>();

	const allFieldsAsString = JSON.stringify(watch());

	useEffect(() => {
		if (error && error.statusCode === 500) {
			throw error;
		}
		setLoginError(error);
	}, [error]);
	useEffect(() => {
		setLoginError(undefined);
	}, [allFieldsAsString]);

	return (
		<section className="hero is-fullheight is-warning">
			<div className="hero-body has-text-centered">
				<div className="container">
					<Logo variant="black" className="icon is-large" />
					<h1 className="title has-text-black">
						<FormattedMessage id="Master_Unauthenticated_Header" />
					</h1>
					<div className="columns is-centered">
						<div className="column is-half">
							<div className="notification is-white">
								<h2 className="title has-text-black is-4">
									<FormattedMessage id="User_Login_Title" />
								</h2>
								<form
									method="post"
									onSubmit={handleSubmit(login)}
								>
									<div className="field">
										<label className="label">
											<FormattedMessage id="User_Login_Username" />
										</label>
										<div className="control has-icons-left">
											<input
												className={`input ${
													errors.username
														? "is-danger"
														: ""
												}`}
												type="text"
												autoComplete="username"
												{...register("username", {
													required: true,
												})}
											/>
											<span className="icon is-left pt-2">
												<Icon
													size={1}
													path={mdiAccount}
												/>
											</span>
										</div>
										{errors.username && (
											<Help>
												<FormattedMessage id="ValidationError_Required" />
											</Help>
										)}
									</div>
									<div className="field">
										<label className="label">
											<FormattedMessage id="User_Login_Password" />
										</label>
										<div className="control has-icons-left">
											<input
												type="password"
												autoComplete="current-password"
												placeholder="********"
												className={`input ${
													errors.password
														? "is-danger"
														: ""
												}`}
												{...register("password", {
													required: true,
												})}
											/>
											<span className="icon is-left pt-2">
												<Icon size={1} path={mdiLock} />
											</span>
										</div>
										{errors.password && (
											<Help>
												<FormattedMessage id="ValidationError_Required" />
											</Help>
										)}
									</div>
									{loginError && (
										<Notification>
											<FormattedMessage
												id={
													loginError.statusCode ===
													401
														? "Login_InvalidUsernameOrPassword"
														: "Login_OtherError"
												}
											/>
											{isDevEnv &&
												loginError.statusCode !==
													401 && (
													<p>
														{JSON.stringify(
															loginError
														)}
													</p>
												)}
										</Notification>
									)}
									<div className="buttons is-centered is-flex is-flex-direction-column">
										<button className="button is-black is-medium">
											<FormattedMessage id="User_Login_LogIn" />
										</button>
										<Link
											to={
												DynamicRoutes.PasswordReset
													.request
											}
										>
											<FormattedMessage id="User_Login_ForgotPassword" />
										</Link>
										<Link
											to={
												DynamicRoutes.UserRegistration
													.edit.route
											}
										>
											<FormattedMessage id="User_Login_Registration" />
										</Link>
									</div>
								</form>
							</div>
						</div>
					</div>
				</div>
			</div>
			<LoginFooter />
		</section>
	);
}

export default Login;

function LoginFooter() {
	const { locale, setLocale } = useLocale();

	const buttons = [
		{ name: "Suomi", locale: Locale.fi },
		{ name: "Svenska", locale: Locale.sv },
		{ name: "English", locale: Locale.en },
	];
	return (
		<div className="hero-foot">
			<div className="block content has-text-centered">
				<p>
					<FormattedMessage id="Siipikarjaliitto" />
				</p>
				<div className="columns is-centered">
					<div className="column buttons has-addons">
						{buttons.map((button, index) => (
							<button
								key={index}
								className={`button is-small${
									button.locale !== locale ? " is-black" : ""
								}`}
								onClick={() => setLocale(button.locale)}
							>
								{button.name}
							</button>
						))}
					</div>
				</div>
			</div>
			<div className="block" />
		</div>
	);
}

function Help({ children }: { children: ReactNode }) {
	return <p className="help is-danger">{children}</p>;
}

function Notification({ children }: { children: ReactNode }) {
	return <div className="notification is-danger p-2">{children}</div>;
}
